import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { ResultService } from "../services/Results/Result";

const statusStyles = {
  pending_verification: {
    label: "Pending",
    bgcolor: "rgba(255,193,7,0.15)",
    color: "#ffca28"
  },
  verified: {
    label: "Verified",
    bgcolor: "rgba(76,175,80,0.15)",
    color: "#66bb6a"
  },
  paid: {
    label: "Success",
    bgcolor: "rgba(33,150,243,0.15)",
    color: "#42a5f5"
  }
};

const maskTicketID = (code) => {
  if (!code) return "";
  if (code.length <= 5) return code;

  const start = code.slice(0, 3);
  const end = code.slice(-2);

  return `${start}****${end}`;
};

const formatDrawDate = (value) => {
  if (!value) return "No draw date";

  return new Date(value).toLocaleString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

export default function Winners() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDrawId, setSelectedDrawId] = useState(null);
  const [page, setPage] = useState(1);

  const winnersPerPage = 5;

  useEffect(() => {
    const fetchWinners = async () => {
      setLoading(true);

      try {
        const res = await ResultService.winnings();
        const nextWinners = Array.isArray(res?.data) ? res.data : [];

        setWinners(nextWinners);

        if (nextWinners.length) {
          const latestDrawId = nextWinners[0]?.draw?.id || null;
          setSelectedDrawId(latestDrawId);
        }
      } catch (error) {
        console.log("Error fetching winners:", error);
      }

      setLoading(false);
    };

    fetchWinners();
  }, []);

  const groupedDraws = useMemo(() => {
    const drawMap = new Map();

    winners.forEach((item) => {
      const draw = item.draw;
      if (!draw?.id) return;

      if (!drawMap.has(draw.id)) {
        drawMap.set(draw.id, {
          id: draw.id,
          draw_code: draw.draw_code,
          draw_date: draw.draw_date,
          winner_prize_amount: item.amount,
          winners: []
        });
      }

      drawMap.get(draw.id).winners.push(item);
    });

    return Array.from(drawMap.values()).sort(
      (a, b) => new Date(b.draw_date) - new Date(a.draw_date)
    );
  }, [winners]);

  const latestDraw = groupedDraws[0] || null;
  const recentDraws = groupedDraws.slice(0, 3);

  const activeDraw =
    groupedDraws.find((draw) => Number(draw.id) === Number(selectedDrawId)) || latestDraw;

  const totalPages = Math.ceil((activeDraw?.winners.length || 0) / winnersPerPage) || 1;

  const paginatedWinners = activeDraw
    ? activeDraw.winners.slice((page - 1) * winnersPerPage, page * winnersPerPage)
    : [];

  const handleSelectDraw = (drawId) => {
    setSelectedDrawId(drawId);
    setPage(1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        p: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 560 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} mb={2}>
          <Chip
            label="Most Recent Result"
            sx={{
              bgcolor: "#0b2a4a",
              color: "#ffcc33",
              fontWeight: 600,
              fontSize: 12
            }}
          />

          <Chip
            label={activeDraw ? formatDrawDate(activeDraw.draw_date) : "Select draw date"}
            sx={{
              bgcolor: "#0b2a4a",
              color: "#cfd8dc",
              fontSize: 12,
              maxWidth: 220,
              "& .MuiChip-label": {
                whiteSpace: "normal",
                lineHeight: 1.2
              }
            }}
          />
        </Stack>

        {!loading && recentDraws.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <TextField
              select
              size="small"
              value={selectedDrawId || recentDraws[0]?.id || ""}
              onChange={(event) => handleSelectDraw(event.target.value)}
              sx={{
                minWidth: { xs: 180, md: 220 },
                bgcolor: "#0b2a4a",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": {
                    borderColor: "#ffcc33"
                  }
                },
                "& .MuiSelect-select": {
                  color: "#fff"
                },
                "& .MuiSvgIcon-root": {
                  color: "#ffcc33"
                }
              }}
            >
              {recentDraws.map((draw) => (
                <MenuItem key={draw.id} value={draw.id} sx={{ color: "#fff", bgcolor: "#0b2a4a" }}>
                  {new Date(draw.draw_date).toLocaleDateString("en-NG", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit"
                  })}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        <Card
          sx={{
            background: "linear-gradient(180deg,#0b2a4a,#071f36)",
            borderRadius: 1,
            p: 3
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: "#ffffff" }}>
            Draw Results
          </Typography>

          <Box mt={3}>
            {loading ? (
              <>
                <Skeleton
                  variant="rectangular"
                  height={120}
                  sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 2, borderRadius: 1 }}
                />
                {[...Array(5)].map((n) => (
                  <Skeleton
                    key={n}
                    variant="rectangular"
                    height={55}
                    sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 2, borderRadius: 1 }}
                  />
                ))}
              </>
            ) : !latestDraw ? (
              <Alert severity="info">No draw results available yet.</Alert>
            ) : (
              <>
                <Card
                  sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 1,
                    bgcolor: "#102f52",
                    border: "1px solid #1e3a5f"
                  }}
                >
                  <Typography sx={{ color: "#ffcc33", fontWeight: 700, fontSize: 14 }}>
                    Most Recent Draw Result
                  </Typography>
                  <Typography sx={{ color: "#ffffff", fontWeight: 700, mt: 1 }}>
                    {activeDraw?.draw_code}
                  </Typography>
                  <Typography sx={{ color: "#b0bec5", mt: 0.8, fontSize: 13 }}>
                    {formatDrawDate(activeDraw?.draw_date)}
                  </Typography>
                  <Typography sx={{ color: "#5fd068", mt: 1.2, fontWeight: 700 }}>
                    Winners: {activeDraw?.winners.length || 0}
                  </Typography>
                </Card>

                <Stack spacing={2}>
                  {paginatedWinners.map((item, index) => {
                    const status = statusStyles[item.status] || statusStyles.pending_verification;
                    const serialNumber = (page - 1) * winnersPerPage + index + 1;

                    return (
                      <Box
                        key={item.id}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          bgcolor: "#0f2d4d",
                          border: "1px solid #1e3a5f",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Box sx={{ position: "relative", minWidth: 0, pl: 4 }}>
                          <Typography
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#ffcc33",
                              fontWeight: 700,
                              fontSize: 14
                            }}
                          >
                            {serialNumber}.
                          </Typography>
                          <Typography
                            sx={{
                              color: "#ffffff",
                              fontWeight: 600,
                              fontSize: 14
                            }}
                          >
                            {item.winner_name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#b0bec5",
                              fontSize: 12
                            }}
                          >
                            {item.ticket_preview || maskTicketID("")}
                          </Typography>
                        </Box>

                        <Box textAlign="right">
                          <Typography
                            sx={{
                              color: "#5fd068",
                              fontWeight: 700,
                              fontSize: 15
                            }}
                          >
                            ₦{Number(item.amount || 0).toLocaleString()}
                          </Typography>

                          <Chip
                            size="small"
                            label={status.label}
                            sx={{
                              mt: 0.5,
                              bgcolor: status.bgcolor,
                              color: status.color,
                              fontSize: 11
                            }}
                          />
                          {item.status !== "paid" && item.settlement_available_at ? (
                            <Typography sx={{ color: "#b0bec5", mt: 0.5, fontSize: 10 }}>
                              Available {new Date(item.settlement_available_at).toLocaleString("en-NG")}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                {activeDraw?.winners.length > winnersPerPage && (
                  <Stack alignItems="center" mt={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(event, value) => setPage(value)}
                      sx={{
                        "& .MuiPaginationItem-root": { color: "#fff" },
                        "& .Mui-selected": {
                          bgcolor: "#ffcc33 !important",
                          color: "#003300 !important"
                        }
                      }}
                    />
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
